import keycloak from "../auth/keycloak";
import type {
  Me,
  Part,
  PartDetail,
  PartClassCoverage,
  Supplier,
  Requirement,
  RFQ,
  Quote,
  PagedResponse,
  Recommendation,
  ReanalyzeResult,
  ApproveResponse,
  PurchaseOrder,
  Shipment,
  ReceivingItem,
  TraceDocument,
  InspectionDecision,
  TraxWorkOrder,
  TraxImportResult,
  AnalyticsOverview,
  SpendBySupplier,
  SpendMonthly,
  SpendByCategory,
  RFQMetrics,
  AOGMetrics,
  SavingsMetrics,
  LeadTimeBySupplier,
  SupplierScorecard,
  PdfQuoteUploadResult,
  AgentDecision,
  ConsolidationGroup,
  VendorOptimizationRun,
  Negotiation,
  AutonomousPolicy,
  PagedAutonomousLog,
  KillSwitchResponse,
  StrictResidencyResponse,
  AuditEvent,
  User,
  UserCreatePayload,
  UserRoleName,
  SupplierDetail,
  SupplierCreatePayload,
  SupplierUpdatePayload,
  ConnectorHealthSummary,
  ConnectorConfig,
  ConnectorConfigCreatePayload,
  ConnectorConfigUpdatePayload,
  LiveHealthResult,
} from "./types";

const BASE = "/api/v1";

export function authHeaders(): Record<string, string> {
  return keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
}

/** An HTTP error carrying the status and the API's `detail` separately.
 *
 * The message keeps its original shape so existing callers that only read
 * `.message` are unaffected; `status` and `detail` let a caller react to a
 * specific outcome — a 409 from the quote review flag, say — instead of
 * pattern-matching prose.
 */
export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(message: string, status: number, detail: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  useInternalToken = false,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...authHeaders(),
  };
  if (useInternalToken) {
    const tok = import.meta.env.VITE_INTERNAL_API_TOKEN;
    if (tok) headers["X-Internal-Token"] = tok;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed?.detail === "string") detail = parsed.detail;
    } catch {
      // Not JSON — keep the raw body as the detail.
    }
    throw new ApiError(
      `${method} ${path} → HTTP ${res.status}: ${text}`,
      res.status,
      detail,
    );
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const api = {
  getMe: () => request<Me>("GET", "/me"),

  listParts: () => request<PagedResponse<Part>>("GET", "/parts?limit=100"),

  /** Browse the part master. Filters are applied server-side, so `total`
   *  describes the filtered population and the pager is meaningful. */
  listPartsDetail: (params?: {
    skip?: number;
    limit?: number;
    q?: string;
    part_class?: string;
    ata_chapter?: string;
    is_active?: boolean;
  }) => {
    const p = new URLSearchParams();
    if (params?.skip != null) p.set("skip", String(params.skip));
    if (params?.limit != null) p.set("limit", String(params.limit));
    if (params?.q) p.set("q", params.q);
    if (params?.part_class) p.set("part_class", params.part_class);
    if (params?.ata_chapter) p.set("ata_chapter", params.ata_chapter);
    if (params?.is_active != null) p.set("is_active", String(params.is_active));
    const qs = p.toString();
    return request<PagedResponse<PartDetail>>(
      "GET",
      `/parts${qs ? `?${qs}` : ""}`,
    );
  },

  getPartClassCoverage: () =>
    request<PartClassCoverage>("GET", "/parts/class-coverage"),

  listSuppliers: () =>
    request<PagedResponse<Supplier>>("GET", "/suppliers?limit=100"),

  createRequirement: (data: {
    part_id: string;
    quantity: number;
    condition_code: string;
    required_by_date?: string;
    is_aog?: boolean;
    station?: string;
    work_order_ref?: string;
    notes?: string;
  }) => request<Requirement>("POST", "/requirements", data),

  listRequirements: (skip = 0, limit = 100) =>
    request<PagedResponse<Requirement>>(
      "GET",
      `/requirements?skip=${skip}&limit=${limit}`,
    ),

  // Closing demand is a PATCH of `status`. Requires buyer/planner/admin — the
  // same gate as creating a requirement.
  closeRequirement: (id: string, status: "fulfilled" | "cancelled") =>
    request<Requirement>("PATCH", `/requirements/${id}`, { status }),

  listRFQs: (skip = 0, limit = 20) =>
    request<PagedResponse<RFQ>>("GET", `/rfqs?skip=${skip}&limit=${limit}`),

  getRFQ: (id: string) => request<RFQ>("GET", `/rfqs/${id}`),

  // Re-run the agent pipeline over quotes the RFQ already holds. The pipeline
  // normally runs once, started by RFQWorkflow when the response window closes;
  // this is the way back when that did not happen (Temporal outage, agent
  // failure, or a quote that arrived after the RFQ expired empty).
  reanalyzeRFQ: (rfqId: string) =>
    request<ReanalyzeResult>("POST", `/rfqs/${rfqId}/reanalyze`),

  getQuotesByRFQ: (rfqId: string) =>
    request<Quote[]>("GET", `/quotes/rfq/${rfqId}`),
  approveQuote: (
    quoteId: string,
    acknowledgeReview = false,
    acknowledgeTrace = false,
  ) =>
    request<{
      po_id: string;
      po_number: string;
      po_status: string;
      po_needs_manager_approval: boolean;
    }>(
      "POST",
      `/quotes/${quoteId}/approve?acknowledge_review=${acknowledgeReview}` +
        `&acknowledge_trace=${acknowledgeTrace}`,
    ),
  rejectQuote: (quoteId: string) =>
    request<Quote>("PATCH", `/quotes/${quoteId}/reject`),

  listRecommendations: (skip = 0, limit = 20, status?: string) =>
    request<PagedResponse<Recommendation>>(
      "GET",
      `/recommendations?skip=${skip}&limit=${limit}${status ? `&status=${status}` : ""}`,
    ),

  getRecommendationByRFQ: (rfqId: string) =>
    request<Recommendation>("GET", `/recommendations/rfq/${rfqId}`),

  approveRecommendation: (
    id: string,
    feedback?: string,
    acknowledgeReview = false,
    acknowledgeTrace = false,
  ) =>
    request<ApproveResponse>("POST", `/recommendations/${id}/approve`, {
      feedback: feedback ?? null,
      // The API returns 409 rather than a PO when the recommended quote was
      // flagged at import and this is not set.
      acknowledge_review: acknowledgeReview,
      // Likewise for a traceability shortfall — and setting this additionally
      // requires the manager or admin role, so a buyer gets 403 rather than
      // being able to clear it themselves.
      acknowledge_trace: acknowledgeTrace,
    }),

  rejectRecommendation: (id: string, feedback?: string) =>
    request<Recommendation>("POST", `/recommendations/${id}/reject`, {
      feedback: feedback ?? null,
    }),

  overrideRecommendation: (
    id: string,
    overrideQuoteId: string,
    feedback?: string,
  ) =>
    request<Recommendation>("POST", `/recommendations/${id}/override`, {
      override_quote_id: overrideQuoteId,
      feedback: feedback ?? null,
    }),

  listPurchaseOrders: (skip = 0, limit = 20, status?: string) =>
    request<PagedResponse<PurchaseOrder>>(
      "GET",
      `/purchase-orders?skip=${skip}&limit=${limit}${status ? `&status=${status}` : ""}`,
    ),

  getPurchaseOrder: (id: string) =>
    request<PurchaseOrder>("GET", `/purchase-orders/${id}`),

  getPOShipments: (poId: string) =>
    request<Shipment[]>("GET", `/purchase-orders/${poId}/shipments`),

  managerApprovePO: (poId: string, notes?: string) =>
    request<PurchaseOrder>("POST", `/purchase-orders/${poId}/manager-approve`, {
      notes: notes ?? null,
    }),

  managerRejectPO: (poId: string, notes?: string) =>
    request<PurchaseOrder>("POST", `/purchase-orders/${poId}/manager-reject`, {
      notes: notes ?? null,
    }),

  redispatchPO: (poId: string) =>
    request<PurchaseOrder>("POST", `/purchase-orders/${poId}/redispatch`),

  listTraxWorkOrders: () =>
    request<{
      work_orders: TraxWorkOrder[];
      total: number;
      importable: number;
    }>("GET", "/erp/trax/work-orders"),

  importTraxWorkOrders: (workOrderRefs: string[] | null) =>
    request<TraxImportResult>("POST", "/erp/trax/import-work-orders", {
      work_order_refs: workOrderRefs,
    }),

  pullTraxWorkOrders: () =>
    request<TraxImportResult>("POST", "/erp/trax/pull-work-orders"),

  uploadPdfQuote: async (
    rfqId: string,
    supplierId: string,
    file: File,
  ): Promise<PdfQuoteUploadResult> => {
    const tok = import.meta.env.VITE_INTERNAL_API_TOKEN;
    const headers: Record<string, string> = { ...authHeaders() };
    if (tok) headers["X-Internal-Token"] = tok;
    const form = new FormData();
    form.append("rfq_id", rfqId);
    form.append("supplier_id", supplierId);
    form.append("file", file);
    const res = await fetch(`${BASE}/inbound/quotes/pdf`, {
      method: "POST",
      headers,
      body: form,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(`POST /inbound/quotes/pdf → HTTP ${res.status}: ${text}`);
    }
    return res.json() as Promise<PdfQuoteUploadResult>;
  },

  // ── Receiving inspection ────────────────────────────────────────────────
  listReceivingQueue: (includeClosed = false) =>
    request<ReceivingItem[]>(
      "GET",
      `/receiving/queue?include_closed=${includeClosed}`,
    ),

  getShipmentDocuments: (shipmentId: string) =>
    request<TraceDocument[]>("GET", `/receiving/${shipmentId}/documents`),

  /**
   * The supplier PDFs behind an RFQ's quotes, for the whole RFQ at once.
   * Group by `quote_id` — a quote that arrived structured has none.
   */
  getQuoteDocuments: (rfqId: string) =>
    request<TraceDocument[]>("GET", `/quotes/rfq/${rfqId}/documents`),

  reconcileShipment: (shipmentId: string) =>
    request<ReceivingItem>("POST", `/receiving/${shipmentId}/reconcile`),

  /**
   * Everything that happened to this shipment — its verdicts, its certificates
   * and the goods-in — already interleaved. Restricted to finance/manager/admin
   * server-side, matching `/audit-events`.
   */
  getShipmentHistory: (shipmentId: string) =>
    request<AuditEvent[]>("GET", `/receiving/${shipmentId}/history`),

  recordInspectionVerdict: (
    shipmentId: string,
    decision: InspectionDecision,
    notes?: string,
  ) =>
    request<ReceivingItem>("POST", `/receiving/${shipmentId}/verdict`, {
      decision,
      notes: notes ?? null,
    }),

  listPODocuments: (poId: string) =>
    request<TraceDocument[]>("GET", `/purchase-orders/${poId}/documents`),

  /**
   * Set a certificate aside as not belonging to the shipment, or put it back.
   *
   * The server re-runs the checks over the remaining pack before responding, so
   * the caller must invalidate the queue as well as the document list — the
   * findings on the shipment change as a result of this call.
   */
  setTraceDocumentExclusion: (
    documentId: string,
    excluded: boolean,
    reason?: string,
  ) =>
    request<TraceDocument>("POST", `/trace-documents/${documentId}/exclusion`, {
      excluded,
      reason: reason ?? null,
    }),

  /**
   * Open the stored original of a certificate in a new tab.
   *
   * A plain `<a href>` cannot be used, and the reason is worth stating: the
   * download is authenticated like every other API call, and the browser does
   * not attach the bearer token to a link navigation. So the bytes are fetched
   * with the token and handed to the tab as an object URL.
   *
   * The tab is opened *before* the await, because a popup blocker allows a
   * window opened during a click and blocks one opened after a network round
   * trip. It is opened blank and navigated once the blob exists.
   */
  openTraceDocument: async (documentId: string): Promise<void> => {
    const tab = window.open("", "_blank");
    try {
      const res = await fetch(
        `${BASE}/trace-documents/${documentId}/download`,
        {
          headers: { ...authHeaders() },
        },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        let detail = text;
        try {
          const parsed = JSON.parse(text);
          if (typeof parsed?.detail === "string") detail = parsed.detail;
        } catch {
          // Not JSON — keep the raw body as the detail.
        }
        throw new ApiError(
          `GET /trace-documents/${documentId}/download → HTTP ${res.status}`,
          res.status,
          detail,
        );
      }
      const url = URL.createObjectURL(await res.blob());
      if (tab) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
      // Long enough for the tab to have loaded it; the blob would otherwise be
      // held for the lifetime of the page.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      throw err;
    }
  },

  uploadPODocument: async (
    poId: string,
    file: File,
    shipmentId?: string,
  ): Promise<TraceDocument> => {
    const form = new FormData();
    form.append("file", file);
    if (shipmentId) form.append("shipment_id", shipmentId);
    const res = await fetch(`${BASE}/purchase-orders/${poId}/documents`, {
      method: "POST",
      headers: { ...authHeaders() },
      body: form,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(
        `POST /purchase-orders/${poId}/documents → HTTP ${res.status}: ${text}`,
      );
    }
    return res.json() as Promise<TraceDocument>;
  },

  simulateShippingNotice: (data: {
    po_number: string;
    tracking_number?: string;
    carrier?: string;
    shipped_at?: string;
    estimated_delivery?: string;
    notes?: string;
  }) =>
    request<{ accepted: boolean; po_number: string; status: string }>(
      "POST",
      "/inbound/shipping-notices",
      data,
      true,
    ),

  simulateReceiptConfirmation: (data: {
    po_number: string;
    erp_po_ref?: string;
    quantity_received: number;
    received_at?: string;
    notes?: string;
  }) =>
    request<{ accepted: boolean; po_number: string; status: string }>(
      "POST",
      "/inbound/receipt-confirmations",
      data,
      true,
    ),

  getAnalyticsOverview: (days = 90) =>
    request<AnalyticsOverview>("GET", `/analytics/overview?days=${days}`),

  getSpendBySupplier: (days = 90, limit = 10) =>
    request<{
      period_days: number;
      total_spend_usd: number;
      by_supplier: SpendBySupplier[];
    }>("GET", `/analytics/spend?days=${days}&limit=${limit}`),

  getSpendMonthly: (days = 180) =>
    request<{ period_days: number; monthly: SpendMonthly[] }>(
      "GET",
      `/analytics/spend/monthly?days=${days}`,
    ),

  getSpendByCategory: (days = 90) =>
    request<{ period_days: number; by_category: SpendByCategory[] }>(
      "GET",
      `/analytics/spend/by-category?days=${days}`,
    ),

  getRFQMetrics: (days = 90) =>
    request<RFQMetrics>("GET", `/analytics/rfq-metrics?days=${days}`),

  getAOGMetrics: (days = 90) =>
    request<AOGMetrics>("GET", `/analytics/aog-metrics?days=${days}`),

  getSavings: (days = 90) =>
    request<SavingsMetrics>("GET", `/analytics/savings?days=${days}`),

  getLeadTime: (days = 90) =>
    request<{ period_days: number; by_supplier: LeadTimeBySupplier[] }>(
      "GET",
      `/analytics/lead-time?days=${days}`,
    ),

  getSupplierScorecards: () =>
    request<{ scorecards: SupplierScorecard[]; total: number }>(
      "GET",
      "/analytics/supplier-scorecards",
    ),

  recomputeScorecards: () =>
    request<{ recomputed: number; message: string }>(
      "POST",
      "/analytics/supplier-scorecards/recompute",
    ),

  runAnalyticsETL: () =>
    request<{ workflow_id: string; message: string }>(
      "POST",
      "/analytics/etl/run",
    ),

  exportCSV: (report: "spend" | "scorecards" | "rfq", days = 90) => {
    const url = `/api/v1/analytics/export/csv?report=${report}&days=${days}`;
    window.open(url, "_blank");
  },

  listAgentDecisions: (skip = 0, limit = 20, agentType?: string) =>
    request<PagedResponse<AgentDecision>>(
      "GET",
      `/agent-decisions?skip=${skip}&limit=${limit}${agentType ? `&agent_type=${encodeURIComponent(agentType)}` : ""}`,
    ),

  getAgentDecision: (id: string) =>
    request<AgentDecision>("GET", `/agent-decisions/${id}`),

  listConsolidationOpportunities: (status?: string) =>
    request<PagedResponse<ConsolidationGroup>>(
      "GET",
      `/consolidation/opportunities${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    ),
  submitConsolidatedRFQ: (groupId: string) =>
    request<{ group_id: string; consolidated_rfq_id: string; message: string }>(
      "POST",
      `/consolidation/opportunities/${groupId}/submit`,
    ),
  dismissConsolidationOpportunity: (groupId: string) =>
    request<void>("POST", `/consolidation/opportunities/${groupId}/dismiss`),
  triggerConsolidationScan: () =>
    request<{ workflow_id: string; message: string }>(
      "POST",
      "/consolidation/trigger",
    ),

  listVendorOptimizationRuns: () =>
    request<PagedResponse<VendorOptimizationRun>>(
      "GET",
      "/vendor-optimization",
    ),
  getVendorOptimizationRun: (id: string) =>
    request<VendorOptimizationRun>("GET", `/vendor-optimization/${id}`),
  triggerVendorOptimization: (days = 90) =>
    request<{ workflow_id: string; message: string }>(
      "POST",
      `/vendor-optimization/trigger?analysis_period_days=${days}`,
    ),
  dismissVendorOptimizationRun: (id: string) =>
    request<void>("POST", `/vendor-optimization/${id}/dismiss`),
  applyVendorOptimizationRun: (id: string) =>
    request<void>("POST", `/vendor-optimization/${id}/apply`),

  listNegotiations: (status?: string) =>
    request<PagedResponse<Negotiation>>(
      "GET",
      `/negotiations${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    ),
  getNegotiation: (id: string) =>
    request<Negotiation>("GET", `/negotiations/${id}`),
  startNegotiation: (rfqId: string, quoteId: string, notes?: string) =>
    request<{ negotiation: Negotiation; workflow_id: string }>(
      "POST",
      "/negotiations",
      {
        rfq_id: rfqId,
        quote_id: quoteId,
        notes,
      },
    ),
  approveNegotiationCounterOffer: (id: string) =>
    request<Negotiation>("POST", `/negotiations/${id}/approve`),
  acceptNegotiation: (
    id: string,
    acknowledgeReview = false,
    acknowledgeTrace = false,
  ) =>
    request<{
      po_id: string;
      po_number: string;
      po_status: string;
      po_needs_manager_approval: boolean;
      already_existed: boolean;
    }>(
      "POST",
      `/negotiations/${id}/accept?acknowledge_review=${acknowledgeReview}` +
        `&acknowledge_trace=${acknowledgeTrace}`,
    ),
  cancelNegotiation: (id: string) =>
    request<void>("POST", `/negotiations/${id}/cancel`),
  injectSupplierResponse: (
    id: string,
    content: string,
    proposedPrice?: number,
  ) =>
    request<Negotiation>("POST", `/negotiations/${id}/supplier-response`, {
      content,
      proposed_price: proposedPrice,
    }),

  // Phase 9 — Autonomous Ordering
  getAutonomousPolicy: () =>
    request<AutonomousPolicy>("GET", "/autonomous-ordering/policy"),
  upsertAutonomousPolicy: (data: Partial<AutonomousPolicy>) =>
    request<AutonomousPolicy>("PUT", "/autonomous-ordering/policy", data),
  killSwitchAutonomousOrdering: () =>
    request<KillSwitchResponse>(
      "POST",
      "/autonomous-ordering/policy/kill-switch",
    ),
  enableAutonomousOrdering: () =>
    request<KillSwitchResponse>("POST", "/autonomous-ordering/policy/enable"),
  listAutonomousLog: (params?: {
    skip?: number;
    limit?: number;
    anomaly_only?: boolean;
    autonomous_only?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.skip != null) q.set("skip", String(params.skip));
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.anomaly_only) q.set("anomaly_only", "true");
    if (params?.autonomous_only) q.set("autonomous_only", "true");
    const qs = q.toString();
    return request<PagedAutonomousLog>(
      "GET",
      `/autonomous-ordering/log${qs ? `?${qs}` : ""}`,
    );
  },
  getAutonomousLogEntry: (id: string) =>
    request<import("./types").AutonomousOrderLog>(
      "GET",
      `/autonomous-ordering/log/${id}`,
    ),
  triggerAutonomousEvaluation: (recommendationId: string) =>
    request<{
      executed: boolean;
      route_reason: string;
      log_id: string;
      workflow_id: string;
    }>("POST", `/autonomous-ordering/evaluate/${recommendationId}`),
  getStrictResidency: () =>
    request<StrictResidencyResponse>("GET", "/tenants/me/strict-residency"),
  setStrictResidency: (enabled: boolean) =>
    request<StrictResidencyResponse>(
      "PUT",
      `/tenants/me/strict-residency?strict_data_residency=${enabled}`,
    ),

  // ── Audit events (finance, manager, admin) ──────────────────────────────
  listAuditEvents: (params?: {
    skip?: number;
    limit?: number;
    resourceType?: string;
    resourceId?: string;
    userId?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.skip != null) q.set("skip", String(params.skip));
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.resourceType) q.set("resource_type", params.resourceType);
    if (params?.resourceId) q.set("resource_id", params.resourceId);
    if (params?.userId) q.set("user_id", params.userId);
    const qs = q.toString();
    return request<PagedResponse<AuditEvent>>(
      "GET",
      `/audit-events${qs ? `?${qs}` : ""}`,
    );
  },

  // ── Administration — Users (admin only) ─────────────────────────────────
  listUsers: (skip = 0, limit = 100) =>
    request<PagedResponse<User>>("GET", `/users?skip=${skip}&limit=${limit}`),
  createUser: (data: UserCreatePayload) =>
    request<User>("POST", "/users", data),
  setUserRoles: (id: string, roles: UserRoleName[]) =>
    request<User>("PUT", `/users/${id}/roles`, { roles }),
  setUserActive: (id: string, isActive: boolean) =>
    request<User>("PATCH", `/users/${id}`, { is_active: isActive }),
  deleteUser: (id: string) => request<void>("DELETE", `/users/${id}`),

  // ── Administration — Suppliers (admin only for writes) ──────────────────
  listSuppliersDetail: (skip = 0, limit = 100) =>
    request<PagedResponse<SupplierDetail>>(
      "GET",
      `/suppliers?skip=${skip}&limit=${limit}`,
    ),
  createSupplier: (data: SupplierCreatePayload) =>
    request<SupplierDetail>("POST", "/suppliers", data),
  updateSupplier: (id: string, data: SupplierUpdatePayload) =>
    request<SupplierDetail>("PATCH", `/suppliers/${id}`, data),
  deleteSupplier: (id: string) => request<void>("DELETE", `/suppliers/${id}`),

  // ── Administration — Connectors (admin only) ────────────────────────────
  listConnectors: () => request<ConnectorHealthSummary[]>("GET", "/connectors"),
  checkConnectorHealth: (name: string) =>
    request<LiveHealthResult>(
      "GET",
      `/connectors/${encodeURIComponent(name)}/health`,
    ),
  listConnectorConfigs: () =>
    request<ConnectorConfig[]>("GET", "/connectors/configs"),
  createConnectorConfig: (data: ConnectorConfigCreatePayload) =>
    request<ConnectorConfig>("POST", "/connectors/configs", data),
  updateConnectorConfig: (name: string, data: ConnectorConfigUpdatePayload) =>
    request<ConnectorConfig>(
      "PATCH",
      `/connectors/configs/${encodeURIComponent(name)}`,
      data,
    ),
  deleteConnectorConfig: (name: string) =>
    request<void>("DELETE", `/connectors/configs/${encodeURIComponent(name)}`),
};
